import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import clsx from 'clsx';

import { api } from '../api';
import {
  invalidateEventQueries,
  optimisticAddEvent,
  optimisticRemoveEvent,
  optimisticUpdateEvent
} from '../event-cache';
import { retintEventBackground, useResolvedTheme } from '../theme';
import { endFromInput, endToInput, fromLocalInput, toLocalInput } from '../event-day';
import type {
  Calendar,
  CalendarEvent,
  CountdownMode,
  DeleteScope,
  EditScope,
  EventDetail
} from '../types';
import Popover, { type AnchorRect } from './Popover';

interface Props {
  eventId: string;
  anchor: AnchorRect;
  onClose: () => void;
}

const CONFIRM_DELETE_TIMEOUT_MS = 3000;

// undefined for a key = "no draft change for this field" (fall through to the
// canonical event value). All-day end-exclusive normalisation happens at save
// time, not while editing.
type DraftPatch = {
  summary?: string;
  location?: string;
  description?: string;
  all_day?: boolean;
  start_time?: string;
  end_time?: string;
};

export default function EventPanel({ eventId, anchor, onClose }: Props) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['event', eventId],
    queryFn: () => api.get<EventDetail>(`/api/events/${eventId}`),
    placeholderData: () => {
      // Render instantly from the list cache; description/time_zone fill in
      // when the GET resolves.
      for (const [, list] of qc.getQueriesData<CalendarEvent[]>({ queryKey: ['events'] })) {
        const hit = list?.find((ev) => ev.id === eventId);
        if (hit) {
          return {
            ...hit,
            description: null,
            time_zone: null,
            recurring_event_id: null
          };
        }
      }
      return undefined;
    }
  });
  const calsQ = useQuery({
    queryKey: ['calendars'],
    queryFn: () => api.get<Calendar[]>('/api/calendars')
  });

  const [draft, setDraft] = useState<DraftPatch>({});
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [copied, setCopied] = useState(false);
  const [deleteScope, setDeleteScope] = useState<DeleteScope>('this');
  const [error, setError] = useState<string | null>(null);
  const [scopeDialog, setScopeDialog] = useState(false);
  // Shown when a repeating event is flagged: one occurrence, or all of them.
  const [pickingCountdown, setPickingCountdown] = useState(false);
  const confirmTimer = useRef<number | null>(null);
  const copiedTimer = useRef<number | null>(null);

  const e = q.data;
  const isRecurring = !!e?.is_recurring_instance;
  const calendar = e ? calsQ.data?.find((c) => c.id === e.calendar_id) ?? null : null;
  const writable = calendar
    ? calendar.access_role === 'owner' || calendar.access_role === 'writer'
    : false;

  useEffect(
    () => () => {
      if (confirmTimer.current) window.clearTimeout(confirmTimer.current);
      if (copiedTimer.current) window.clearTimeout(copiedTimer.current);
    },
    []
  );

  // What the panel is showing right now: canonical event with the user's
  // pending edits merged on top.
  const view = useMemo(() => {
    if (!e) return null;
    return {
      summary: draft.summary ?? e.summary ?? '',
      location: draft.location ?? e.location ?? '',
      description: draft.description ?? e.description ?? '',
      all_day: draft.all_day ?? e.all_day,
      start_time: draft.start_time ?? e.start_time,
      end_time: draft.end_time ?? e.end_time
    };
  }, [e, draft]);

  // Only the fields that actually differ from canonical — used both for the
  // PATCH body and to decide whether the close needs to flush.
  const delta = useMemo<DraftPatch>(() => {
    if (!e) return {};
    const d: DraftPatch = {};
    if (draft.summary !== undefined && draft.summary !== (e.summary ?? '')) {
      d.summary = draft.summary;
    }
    if (draft.location !== undefined && draft.location !== (e.location ?? '')) {
      d.location = draft.location;
    }
    if (draft.description !== undefined && draft.description !== (e.description ?? '')) {
      d.description = draft.description;
    }
    if (draft.all_day !== undefined && draft.all_day !== e.all_day) {
      d.all_day = draft.all_day;
    }
    if (draft.start_time !== undefined && draft.start_time !== e.start_time) {
      d.start_time = draft.start_time;
    }
    if (draft.end_time !== undefined && draft.end_time !== e.end_time) {
      d.end_time = draft.end_time;
    }
    return d;
  }, [e, draft]);

  const dirty = Object.keys(delta).length > 0;
  const timeChanged =
    delta.all_day !== undefined ||
    delta.start_time !== undefined ||
    delta.end_time !== undefined;

  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.patch(`/api/events/${eventId}`, body),
    onSuccess: () => {
      setError(null);
      invalidateEventQueries(qc);
      qc.invalidateQueries({ queryKey: ['event', eventId] });
    },
    onError: (err: Error) => {
      setError(err.message || 'save failed');
      invalidateEventQueries(qc);
      qc.invalidateQueries({ queryKey: ['event', eventId] });
    }
  });

  const countdown = useMutation({
    mutationFn: (mode: CountdownMode | null) =>
      api.put(`/api/events/${eventId}/countdown`, { mode }),
    onMutate: (mode: CountdownMode | null) => {
      optimisticUpdateEvent(qc, eventId, { countdown: mode });
    },
    onSettled: () => {
      invalidateEventQueries(qc);
      qc.invalidateQueries({ queryKey: ['countdowns'] });
      qc.invalidateQueries({ queryKey: ['event', eventId] });
    }
  });

  function setCountdown(mode: CountdownMode | null) {
    setPickingCountdown(false);
    countdown.mutate(mode);
  }

  function toggleCountdown() {
    if (e?.countdown) setCountdown(null);
    else if (isRecurring) setPickingCountdown((v) => !v);
    else setCountdown('once');
  }

  function flushSave(scope: EditScope) {
    if (!e || !dirty) return;
    const body: Record<string, unknown> = { scope };
    const listPatch: Partial<CalendarEvent> = {};
    const detailPatch: Partial<EventDetail> = {};

    if (delta.summary !== undefined) {
      body.summary = delta.summary;
      listPatch.summary = delta.summary || null;
    }
    if (delta.location !== undefined) {
      body.location = delta.location;
      listPatch.location = delta.location || null;
    }
    if (delta.description !== undefined) {
      body.description = delta.description;
      detailPatch.description = delta.description || null;
    }
    // "this" and "following" both carry time changes — "following" splits the
    // series server-side and starts the new half at the edited time. ("all"
    // can't change time.) Optimistic local patching is only safe for "this";
    // a "following" split reshapes the series, so we let the refetch reconcile.
    if (scope === 'this' || scope === 'following') {
      const optimistic = scope === 'this';
      if (delta.all_day !== undefined) {
        body.all_day = delta.all_day;
        if (optimistic) listPatch.all_day = delta.all_day;
      }
      let startIso = delta.start_time;
      let endIso = delta.end_time;
      // Google's all-day end is exclusive — bump by one day if the user left
      // end == start for a single-day all-day event.
      const finalAllDay = delta.all_day ?? e.all_day;
      const finalStart = startIso ?? e.start_time;
      const finalEnd = endIso ?? e.end_time;
      if (finalAllDay && finalStart && finalEnd) {
        const s = new Date(finalStart);
        const en = new Date(finalEnd);
        if (en <= s) {
          endIso = new Date(s.getTime() + 24 * 60 * 60 * 1000).toISOString();
        }
      } else if (!finalAllDay && finalStart && finalEnd) {
        // A timed event with end <= start (e.g. just toggled off all-day, or
        // both inputs left on the same instant) is what Google rejects as
        // "Invalid start time." Give it a one-hour span instead.
        const s = new Date(finalStart);
        const en = new Date(finalEnd);
        if (en <= s) {
          endIso = new Date(s.getTime() + 60 * 60 * 1000).toISOString();
        }
      }
      if (startIso !== undefined) {
        body.start_time = startIso;
        if (optimistic) listPatch.start_time = startIso;
      }
      if (endIso !== undefined) {
        body.end_time = endIso;
        if (optimistic) listPatch.end_time = endIso;
      }
    }

    if (Object.keys(listPatch).length > 0 || Object.keys(detailPatch).length > 0) {
      optimisticUpdateEvent(qc, eventId, listPatch, detailPatch);
    }
    // body always carries `scope`; only call out if there's a real field too.
    if (Object.keys(body).length > 1) {
      save.mutate(body);
    }
    setDraft({});
  }

  // Popover wants to dismiss (Escape, outside click). Decide whether to save
  // first, ask for scope, or just go.
  function requestClose() {
    if (!dirty || !writable) {
      onClose();
      return;
    }
    if (isRecurring) {
      setScopeDialog(true);
      return;
    }
    flushSave('this');
    onClose();
  }

  function pickScope(scope: EditScope) {
    flushSave(scope);
    setScopeDialog(false);
    onClose();
  }

  const remove = useMutation({
    mutationFn: () => api.delete(`/api/events/${eventId}?scope=${deleteScope}`),
    onSettled: () => {
      invalidateEventQueries(qc);
    }
  });

  const move = useMutation({
    mutationFn: (targetCalendarId: string) =>
      api.post<{ id: string }>(`/api/events/${eventId}/move`, {
        calendar_id: targetCalendarId
      }),
    onSuccess: () => {
      invalidateEventQueries(qc);
      qc.invalidateQueries({ queryKey: ['event', eventId] });
      // Move issues a new local row in the destination calendar with a fresh
      // UUID; this panel's eventId now points at a deleted row. Close so a
      // reopen picks up the new id.
      onClose();
    },
    onError: (err: Error) => {
      setError(err.message || 'move failed');
      invalidateEventQueries(qc);
    }
  });

  const duplicate = useMutation({
    mutationFn: () => {
      if (!e) throw new Error('event not loaded');
      return api.post<{ id: string }>('/api/events', {
        calendar_id: e.calendar_id,
        summary: e.summary ?? '',
        description: e.description,
        location: e.location,
        start_time: e.start_time,
        end_time: e.end_time,
        all_day: e.all_day
      });
    },
    onSettled: () => {
      invalidateEventQueries(qc);
    }
  });

  function doCopy() {
    if (!e) return;
    const lines: string[] = [];
    if (e.summary) lines.push(e.summary);
    const when = formatWhen({
      start_time: view?.start_time ?? e.start_time,
      end_time: view?.end_time ?? e.end_time,
      all_day: view?.all_day ?? e.all_day
    });
    if (when) lines.push(when);
    if (e.location) lines.push(e.location);
    if (e.description) lines.push(e.description);
    void navigator.clipboard
      ?.writeText(lines.join('\n'))
      .then(() => {
        setCopied(true);
        if (copiedTimer.current) window.clearTimeout(copiedTimer.current);
        copiedTimer.current = window.setTimeout(() => setCopied(false), 1500);
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Copy failed');
      });
  }

  function doDelete() {
    if (confirmingDelete) {
      if (confirmTimer.current) {
        window.clearTimeout(confirmTimer.current);
        confirmTimer.current = null;
      }
      optimisticRemoveEvent(qc, eventId);
      remove.mutate();
      setDraft({});
      onClose();
      return;
    }
    setConfirmingDelete(true);
    setError(null);
    if (confirmTimer.current) window.clearTimeout(confirmTimer.current);
    confirmTimer.current = window.setTimeout(() => {
      setConfirmingDelete(false);
      confirmTimer.current = null;
    }, CONFIRM_DELETE_TIMEOUT_MS);
  }

  function doDuplicate() {
    if (e) {
      optimisticAddEvent(qc, {
        id: crypto.randomUUID(),
        calendar_id: e.calendar_id,
        summary: e.summary,
        location: e.location,
        start_time: e.start_time,
        end_time: e.end_time,
        all_day: e.all_day,
        status: 'confirmed',
        html_link: null,
        is_recurring_instance: false,
        countdown: null,
        background_color: e.background_color,
        foreground_color: e.foreground_color
      });
    }
    duplicate.mutate();
    setDraft({});
    onClose();
  }

  return (
    <>
      <Popover anchor={anchor} onClose={requestClose} width={380}>
        <header className="flex items-center gap-1 border-b border-[var(--border)] px-3 py-2">
          <div className="flex-1 text-sm font-semibold text-[var(--text-muted)]">Event</div>
          {e && (
            <div className="flex items-center gap-1">
              <IconBtn
                title={
                  e.countdown === 'series'
                    ? 'Stop counting down to each occurrence'
                    : e.countdown
                      ? 'Stop counting down'
                      : isRecurring
                        ? 'Count down to this…'
                        : 'Count down to this'
                }
                onClick={toggleCountdown}
                tone={e.countdown ? 'ok' : 'default'}
              >
                <HourglassIcon />
              </IconBtn>
              <IconBtn
                title={copied ? 'Copied' : 'Copy details'}
                onClick={doCopy}
                tone={copied ? 'ok' : 'default'}
              >
                {copied ? <CheckIcon /> : <CopyIcon />}
              </IconBtn>
              <IconBtn title="Duplicate" onClick={doDuplicate}>
                <DuplicateIcon />
              </IconBtn>
              <IconBtn
                title={confirmingDelete ? 'Click again to confirm' : 'Delete'}
                onClick={doDelete}
                tone={confirmingDelete ? 'danger' : 'default'}
              >
                <TrashIcon />
              </IconBtn>
            </div>
          )}
        </header>

        {pickingCountdown && e && (
          <div className="flex flex-col gap-1 border-b border-[var(--border)] bg-[var(--bg-muted)] px-4 py-2">
            <span className="text-[0.72rem] uppercase tracking-wide text-[var(--text-faint)]">
              Count down to
            </span>
            <div className="inline-flex flex-wrap gap-1">
              {(
                [
                  ['once', 'This occurrence'],
                  ['series', 'Every occurrence']
                ] as [CountdownMode, string][]
              ).map(([mode, label]) => (
                <button
                  key={mode}
                  onClick={() => setCountdown(mode)}
                  className="rounded bg-[var(--bg)] px-3 py-[4px] text-[0.78rem] text-[var(--text)] shadow-sm hover:bg-[var(--bg-hover)]"
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        )}

        {confirmingDelete && isRecurring && e && (
          <div className="flex flex-col gap-1 border-b border-[var(--border)] bg-[var(--bg-muted)] px-4 py-2">
            <span className="text-[0.72rem] uppercase tracking-wide text-[var(--text-faint)]">
              Delete
            </span>
            <div className="inline-flex flex-wrap gap-1">
              {(['this', 'following', 'all'] as DeleteScope[]).map((s) => (
                <button
                  key={s}
                  onClick={() => setDeleteScope(s)}
                  className={clsx(
                    'rounded px-3 py-[4px] text-[0.78rem]',
                    deleteScope === s
                      ? 'bg-[var(--bg)] text-[var(--text)] shadow-sm'
                      : 'text-[var(--text-muted)] hover:bg-[var(--bg-hover)]'
                  )}
                >
                  {s === 'this'
                    ? 'This event'
                    : s === 'following'
                      ? 'This + following'
                      : 'All events'}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-4 py-3">
          {q.isLoading && <div className="text-sm text-[var(--text-faint)]">Loading…</div>}
          {q.isError && (
            <div className="text-sm text-[var(--text-faint)]">
              failed to load event: {(q.error as Error).message}
            </div>
          )}
          {e && view && (
            <div className="flex flex-col gap-3">
              <InlineText
                value={view.summary}
                placeholder="(no title)"
                readOnly={!writable}
                className="text-lg font-semibold leading-tight"
                onCommit={(v) =>
                  setDraft((d) => ({ ...d, summary: v }))
                }
              />

              <InlineTime
                allDay={view.all_day}
                startIso={view.start_time}
                endIso={view.end_time}
                readOnly={!writable}
                onCommit={(next) =>
                  setDraft((d) => ({
                    ...d,
                    all_day: next.all_day,
                    start_time: next.start_time,
                    end_time: next.end_time
                  }))
                }
              />

              {calendar && (
                <CalendarPicker
                  current={calendar}
                  calendars={calsQ.data ?? []}
                  // Recurring instances and read-only source calendars can't
                  // be moved — Google's events.move rejects both.
                  canMove={!isRecurring && writable && !move.isPending}
                  onPick={(id) => move.mutate(id)}
                />
              )}

              <InlineText
                value={view.location}
                placeholder="Add location"
                readOnly={!writable}
                className="text-sm text-[var(--text-muted)]"
                onCommit={(v) => setDraft((d) => ({ ...d, location: v }))}
              />

              <InlineTextarea
                value={view.description}
                placeholder="Add description"
                readOnly={!writable}
                className="text-sm"
                onCommit={(v) => setDraft((d) => ({ ...d, description: v }))}
              />

              {isRecurring && (
                <div className="rounded-md bg-[var(--bg-muted)] px-3 py-2 text-[0.75rem] text-[var(--text-muted)]">
                  Repeating event.
                </div>
              )}
              {e.html_link && (
                <a
                  href={withAuthUser(e.html_link, calendar?.account_email)}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[0.78rem] text-[var(--text-muted)] underline hover:text-[var(--text)]"
                >
                  Open in Google Calendar
                </a>
              )}
            </div>
          )}
          {error && (
            <div className="mt-3 rounded bg-[var(--today-bg)] px-3 py-2 text-[0.78rem] text-[var(--today-fg)]">
              {error}
            </div>
          )}
        </div>
      </Popover>
      {scopeDialog && view && (
        <RecurringScopeDialog
          summary={view.summary}
          timeChanged={timeChanged}
          onPick={pickScope}
          onCancel={() => setScopeDialog(false)}
        />
      )}
    </>
  );
}

interface CalendarPickerProps {
  current: Calendar;
  calendars: Calendar[];
  canMove: boolean;
  onPick: (calendarId: string) => void;
}

function CalendarPicker({ current, calendars, canMove, onPick }: CalendarPickerProps) {
  const theme = useResolvedTheme();
  const [editing, setEditing] = useState(false);
  // Google's events.move is bounded to one account. Limit the picker to
  // writable calendars on the same account; if there are no other options,
  // fall back to a static display so the row isn't a dead button.
  const options = useMemo(
    () =>
      calendars.filter(
        (c) =>
          c.account_id === current.account_id &&
          (c.access_role === 'owner' || c.access_role === 'writer')
      ),
    [calendars, current.account_id]
  );
  const hasAlternatives = options.some((c) => c.id !== current.id);

  const display = (
    <div className="flex items-center gap-2 text-sm text-[var(--text-muted)]">
      <span
        aria-hidden
        className="block h-[10px] w-[10px] flex-shrink-0 rounded-[3px]"
        style={{ background: retintEventBackground(current.background_color, theme) }}
      />
      <span className="overflow-hidden text-ellipsis whitespace-nowrap">{current.summary}</span>
      <span className="text-[var(--text-faint)]">·</span>
      <span className="overflow-hidden text-ellipsis whitespace-nowrap text-[var(--text-faint)]">
        {current.account_email}
      </span>
    </div>
  );

  if (!canMove || !hasAlternatives) return display;

  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        className="-mx-[6px] cursor-text rounded-md px-[6px] py-[2px] text-left hover:bg-[var(--bg-hover)]"
        title="Move to another calendar"
      >
        {display}
      </button>
    );
  }

  return (
    <select
      autoFocus
      value={current.id}
      onChange={(ev) => {
        const next = ev.target.value;
        setEditing(false);
        if (next !== current.id) onPick(next);
      }}
      onBlur={() => setEditing(false)}
      onKeyDown={(ev) => {
        if (ev.key === 'Escape') {
          ev.preventDefault();
          ev.stopPropagation();
          setEditing(false);
        }
      }}
      className="-mx-[6px] w-[calc(100%+12px)] rounded-md bg-[var(--bg-muted)] px-[6px] py-[4px] text-sm outline-none ring-1 ring-[var(--border-strong)]"
    >
      {options.map((c) => (
        <option key={c.id} value={c.id}>
          {c.summary} — {c.account_email}
        </option>
      ))}
    </select>
  );
}

interface InlineTextProps {
  value: string;
  placeholder: string;
  className?: string;
  readOnly?: boolean;
  onCommit: (next: string) => void;
}

function InlineText({ value, placeholder, className, readOnly, onCommit }: InlineTextProps) {
  const [editing, setEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  // Snapshot at edit start so Escape can revert. Per-keystroke commits go
  // straight to the parent draft so a mousedown-driven close never loses the
  // last few characters.
  const originalRef = useRef(value);

  useEffect(() => {
    if (editing && inputRef.current) {
      const el = inputRef.current;
      el.focus();
      const len = el.value.length;
      el.setSelectionRange(len, len);
    }
  }, [editing]);

  function startEditing() {
    originalRef.current = value;
    setEditing(true);
  }

  function cancel() {
    if (originalRef.current !== value) onCommit(originalRef.current);
    setEditing(false);
  }

  if (editing) {
    return (
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(ev) => onCommit(ev.target.value)}
        onBlur={() => setEditing(false)}
        onKeyDown={(ev) => {
          if (ev.key === 'Enter') {
            ev.preventDefault();
            setEditing(false);
          } else if (ev.key === 'Escape') {
            ev.preventDefault();
            ev.stopPropagation();
            cancel();
          }
        }}
        className={clsx(
          '-mx-[6px] w-[calc(100%+12px)] rounded-md bg-[var(--bg-muted)] px-[6px] py-[2px] outline-none ring-1 ring-[var(--border-strong)]',
          className
        )}
      />
    );
  }

  const empty = !value;
  if (readOnly) {
    if (empty) return null;
    return <div className={className}>{value}</div>;
  }
  return (
    <button
      type="button"
      onClick={startEditing}
      className={clsx(
        '-mx-[6px] cursor-text rounded-md px-[6px] py-[2px] text-left hover:bg-[var(--bg-hover)]',
        empty && 'italic text-[var(--text-faint)] hover:text-[var(--text-muted)]',
        className
      )}
    >
      {value || placeholder}
    </button>
  );
}

interface InlineTextareaProps {
  value: string;
  placeholder: string;
  className?: string;
  readOnly?: boolean;
  onCommit: (next: string) => void;
}

function InlineTextarea({ value, placeholder, className, readOnly, onCommit }: InlineTextareaProps) {
  const [editing, setEditing] = useState(false);
  const taRef = useRef<HTMLTextAreaElement>(null);
  const originalRef = useRef(value);

  useLayoutEffect(() => {
    if (editing && taRef.current) {
      const el = taRef.current;
      el.focus();
      const len = el.value.length;
      el.setSelectionRange(len, len);
      autosize(el);
    }
  }, [editing]);

  useLayoutEffect(() => {
    if (editing && taRef.current) autosize(taRef.current);
  }, [editing, value]);

  function autosize(el: HTMLTextAreaElement) {
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }

  function startEditing() {
    originalRef.current = value;
    setEditing(true);
  }

  function cancel() {
    if (originalRef.current !== value) onCommit(originalRef.current);
    setEditing(false);
  }

  if (editing) {
    return (
      <textarea
        ref={taRef}
        value={value}
        onChange={(ev) => onCommit(ev.target.value)}
        onBlur={() => setEditing(false)}
        onKeyDown={(ev) => {
          if (ev.key === 'Escape') {
            ev.preventDefault();
            ev.stopPropagation();
            cancel();
          }
        }}
        rows={3}
        className={clsx(
          '-mx-[6px] w-[calc(100%+12px)] resize-none rounded-md bg-[var(--bg-muted)] px-[6px] py-[4px] outline-none ring-1 ring-[var(--border-strong)]',
          className
        )}
      />
    );
  }

  const empty = !value;
  if (readOnly) {
    if (empty) return null;
    return <div className={clsx('whitespace-pre-wrap', className)}>{value}</div>;
  }
  return (
    <button
      type="button"
      onClick={startEditing}
      className={clsx(
        '-mx-[6px] cursor-text whitespace-pre-wrap rounded-md px-[6px] py-[2px] text-left hover:bg-[var(--bg-hover)]',
        empty && 'italic text-[var(--text-faint)] hover:text-[var(--text-muted)]',
        className
      )}
    >
      {value || placeholder}
    </button>
  );
}

interface InlineTimeProps {
  allDay: boolean;
  startIso: string | null;
  endIso: string | null;
  readOnly: boolean;
  onCommit: (next: { all_day: boolean; start_time: string; end_time: string }) => void;
}

function InlineTime({ allDay, startIso, endIso, readOnly, onCommit }: InlineTimeProps) {
  const [editing, setEditing] = useState(false);
  // Mirrors of the props as local input strings — the inputs need a string,
  // and toggling all-day swaps between two different string formats. Every
  // change here also commits straight to the parent draft so a click-outside
  // never loses an unblurred edit.
  const [draftAllDay, setDraftAllDay] = useState(allDay);
  const [start, setStart] = useState(() => toLocalInput(startIso, allDay));
  const [end, setEnd] = useState(() => endToInput(endIso, allDay));
  const wrapRef = useRef<HTMLDivElement>(null);
  const initialFocusRef = useRef<HTMLInputElement>(null);
  const originalRef = useRef<{ allDay: boolean; startIso: string | null; endIso: string | null }>({
    allDay,
    startIso,
    endIso
  });

  useEffect(() => {
    if (!editing) {
      setDraftAllDay(allDay);
      setStart(toLocalInput(startIso, allDay));
      setEnd(endToInput(endIso, allDay));
    }
  }, [editing, allDay, startIso, endIso]);

  useEffect(() => {
    if (editing) initialFocusRef.current?.focus();
  }, [editing]);

  function commitTriple(a: boolean, s: string, e: string) {
    const startConverted = fromLocalInput(s, a);
    const endConverted = endFromInput(e, a);
    if (!startConverted || !endConverted) return;
    onCommit({
      all_day: a,
      start_time: startConverted,
      end_time: endConverted
    });
  }

  function startEditing() {
    originalRef.current = { allDay, startIso, endIso };
    setEditing(true);
  }

  function cancel() {
    const o = originalRef.current;
    setDraftAllDay(o.allDay);
    setStart(toLocalInput(o.startIso, o.allDay));
    setEnd(endToInput(o.endIso, o.allDay));
    if (o.startIso && o.endIso) {
      onCommit({ all_day: o.allDay, start_time: o.startIso, end_time: o.endIso });
    }
    setEditing(false);
  }

  function onWrapBlur(ev: React.FocusEvent<HTMLDivElement>) {
    // Only exit edit mode when focus actually leaves the wrapper. Moves
    // between the two date inputs shouldn't collapse the editor.
    const next = ev.relatedTarget as Node | null;
    if (next && wrapRef.current?.contains(next)) return;
    setEditing(false);
  }

  function toggleAllDay(next: boolean) {
    const newStart = next ? start.slice(0, 10) : start.length === 10 ? `${start}T09:00` : start;
    const newEnd = next ? end.slice(0, 10) : end.length === 10 ? `${end}T10:00` : end;
    setDraftAllDay(next);
    setStart(newStart);
    setEnd(newEnd);
    commitTriple(next, newStart, newEnd);
  }

  function onStartChange(next: string) {
    setStart(next);
    commitTriple(draftAllDay, next, end);
  }

  function onEndChange(next: string) {
    setEnd(next);
    commitTriple(draftAllDay, start, next);
  }

  if (!editing) {
    const label = formatWhen({ start_time: startIso, end_time: endIso, all_day: allDay }) || '—';
    if (readOnly) return <div className="text-sm text-[var(--text-muted)]">{label}</div>;
    return (
      <button
        type="button"
        onClick={startEditing}
        className="-mx-[6px] cursor-text rounded-md px-[6px] py-[2px] text-left text-sm text-[var(--text-muted)] hover:bg-[var(--bg-hover)]"
      >
        {label}
      </button>
    );
  }

  return (
    <div
      ref={wrapRef}
      onBlur={onWrapBlur}
      onKeyDown={(ev) => {
        if (ev.key === 'Escape') {
          ev.preventDefault();
          ev.stopPropagation();
          cancel();
        } else if (ev.key === 'Enter' && (ev.target as HTMLElement).tagName === 'INPUT') {
          ev.preventDefault();
          setEditing(false);
        }
      }}
      className="flex flex-col gap-2 rounded-md bg-[var(--bg-muted)] px-2 py-2 ring-1 ring-[var(--border-strong)]"
    >
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={draftAllDay}
          onChange={(ev) => toggleAllDay(ev.target.checked)}
        />
        All-day
      </label>
      <Field label="Starts">
        <input
          ref={initialFocusRef}
          type={draftAllDay ? 'date' : 'datetime-local'}
          value={start}
          onChange={(ev) => onStartChange(ev.target.value)}
          className="rounded border border-[var(--border)] bg-[var(--bg)] px-2 py-[6px] text-sm"
        />
      </Field>
      <Field label="Ends">
        <input
          type={draftAllDay ? 'date' : 'datetime-local'}
          value={end}
          onChange={(ev) => onEndChange(ev.target.value)}
          className="rounded border border-[var(--border)] bg-[var(--bg)] px-2 py-[6px] text-sm"
        />
      </Field>
    </div>
  );
}

interface RecurringScopeDialogProps {
  summary: string;
  timeChanged: boolean;
  onPick: (scope: EditScope) => void;
  onCancel: () => void;
}

function RecurringScopeDialog({
  summary,
  timeChanged,
  onPick,
  onCancel
}: RecurringScopeDialogProps) {
  const thisRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const t = window.setTimeout(() => thisRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, []);

  useEffect(() => {
    function onKey(ev: KeyboardEvent) {
      if (ev.key === 'Escape') {
        ev.preventDefault();
        ev.stopPropagation();
        onCancel();
      } else if (ev.key === 'Enter') {
        ev.preventDefault();
        onPick('this');
      }
    }
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [onPick, onCancel]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4"
      onClick={onCancel}
    >
      <div
        className="flex w-full max-w-[420px] flex-col gap-3 rounded-lg border border-[var(--border-strong)] bg-[var(--popover-bg)] p-4 shadow-[var(--shadow-md)]"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        role="alertdialog"
        aria-label="Apply edits to repeating event"
      >
        <div className="text-sm font-semibold">Apply edits to repeating event</div>
        <div className="text-sm text-[var(--text-muted)]">
          “{summary || '(no title)'}” repeats. Save changes to this occurrence, this and all
          following events, or every event in the series?
        </div>
        {timeChanged && (
          <div className="text-[0.78rem] text-[var(--text-faint)]">
            Time changes can’t apply to the whole series — pick this event or this and following.
          </div>
        )}
        <div className="mt-1 flex flex-wrap justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-md px-3 py-[6px] text-[0.85rem] text-[var(--text-muted)] hover:bg-[var(--bg-hover)] hover:text-[var(--text)]"
          >
            Keep editing
          </button>
          <button
            type="button"
            disabled={timeChanged}
            onClick={() => onPick('all')}
            title={
              timeChanged ? 'Time changes can’t apply to the whole series' : undefined
            }
            className="rounded-md border border-[var(--border-strong)] px-3 py-[6px] text-[0.85rem] text-[var(--text)] hover:bg-[var(--bg-hover)] disabled:cursor-not-allowed disabled:opacity-50"
          >
            All events
          </button>
          <button
            type="button"
            onClick={() => onPick('following')}
            className="rounded-md border border-[var(--border-strong)] px-3 py-[6px] text-[0.85rem] text-[var(--text)] hover:bg-[var(--bg-hover)]"
          >
            This + following
          </button>
          <button
            ref={thisRef}
            type="button"
            onClick={() => onPick('this')}
            className="rounded-md bg-[var(--accent)] px-3 py-[6px] text-[0.85rem] text-[var(--accent-fg)] hover:opacity-90"
          >
            This event
          </button>
        </div>
      </div>
    </div>
  );
}

type Tone = 'default' | 'primary' | 'danger' | 'ok';

interface IconBtnProps {
  title: string;
  onClick: () => void;
  disabled?: boolean;
  tone?: Tone;
  children: React.ReactNode;
}

function IconBtn({ title, onClick, disabled, tone = 'default', children }: IconBtnProps) {
  const toneClass =
    tone === 'primary'
      ? 'bg-[var(--accent)] text-[var(--accent-fg)] hover:opacity-90'
      : tone === 'danger'
        ? 'bg-[var(--today-bg)] text-[var(--today-fg)] hover:bg-[var(--today-fg)] hover:text-white'
        : tone === 'ok'
          ? 'text-[var(--accent)]'
          : 'text-[var(--text-muted)] hover:bg-[var(--bg-hover)] hover:text-[var(--text)]';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={title}
      title={title}
      className={clsx(
        'grid h-8 w-8 place-items-center rounded transition-colors disabled:cursor-wait disabled:opacity-50',
        toneClass
      )}
    >
      {children}
    </button>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-1 flex-col gap-1">
      <span className="text-[0.72rem] uppercase tracking-wide text-[var(--text-faint)]">
        {label}
      </span>
      {children}
    </label>
  );
}

function withAuthUser(htmlLink: string, email: string | null | undefined): string {
  if (!email) return htmlLink;
  try {
    const url = new URL(htmlLink);
    url.searchParams.set('authuser', email);
    return url.toString();
  } catch {
    return htmlLink;
  }
}

function formatWhen(e: {
  start_time: string | null;
  end_time: string | null;
  all_day: boolean;
}): string {
  if (!e.start_time) return '';
  const start = new Date(e.start_time);
  const end = e.end_time ? new Date(e.end_time) : null;
  if (e.all_day) {
    // All-day timestamps are UTC midnight (see event-day.ts); read the UTC
    // date parts so the label shows the stored calendar day rather than one
    // shifted by the local offset.
    const startDay = new Date(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate());
    if (!end) return format(startDay, 'EEEE, MMMM d, yyyy');
    // Google's all-day end is exclusive; step back a day for the last day covered.
    const last = new Date(end.getUTCFullYear(), end.getUTCMonth(), end.getUTCDate());
    last.setDate(last.getDate() - 1);
    if (last.toDateString() === startDay.toDateString()) {
      return format(startDay, 'EEEE, MMMM d, yyyy');
    }
    return `${format(startDay, 'MMM d')} – ${format(last, 'MMM d, yyyy')}`;
  }
  const dayPart = format(start, 'EEEE, MMMM d, yyyy');
  const startT = format(start, 'h:mm a').toLowerCase();
  const endT = end ? format(end, 'h:mm a').toLowerCase() : '';
  return `${dayPart} · ${startT}${endT ? ` – ${endT}` : ''}`;
}

const svgProps = {
  viewBox: '0 0 16 16',
  width: 16,
  height: 16,
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.5,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true
};

function HourglassIcon() {
  return (
    <svg
      viewBox="0 0 16 16"
      width="15"
      height="15"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M4.5 2h7M4.5 14h7" />
      <path d="M5 2v2.2c0 1.1 2 2.4 2 3.8s-2 2.7-2 3.8V14" />
      <path d="M11 2v2.2c0 1.1-2 2.4-2 3.8s2 2.7 2 3.8V14" />
    </svg>
  );
}

function CopyIcon() {
  return (
    <svg {...svgProps}>
      <rect x="5" y="5" width="9" height="9" rx="1.5" />
      <path d="M2.5 11V3.5A1.5 1.5 0 0 1 4 2h7.5" />
    </svg>
  );
}

function DuplicateIcon() {
  return (
    <svg {...svgProps}>
      <rect x="2.5" y="2.5" width="9" height="9" rx="1.5" />
      <path d="M11 7.5h2.5a1 1 0 0 1 1 1V13a1 1 0 0 1-1 1H8a1 1 0 0 1-1-1v-2" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg {...svgProps}>
      <path d="M2.5 4h11" />
      <path d="M5.5 4V2.5h5V4" />
      <path d="M4.5 4l.5 9.5a1.5 1.5 0 0 0 1.5 1.5h3a1.5 1.5 0 0 0 1.5-1.5l.5-9.5" />
      <path d="M6.5 6.5v6M9.5 6.5v6" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg {...svgProps}>
      <path d="M3 8.5l3 3 7-7" />
    </svg>
  );
}
